// @flow

import buildExperimentsClients from 'app/common/lib/buildExperimentsClients';
import context from 'app/common/lib/context';
import EmptyExperimentsClient from 'app/common/lib/ExperimentsClient/EmptyExperimentsClient';
import PropTypes from 'prop-types';
import type AExperimentsClient from 'app/common/lib/ExperimentsClient/AExperimentsClient';
import type { VersionTuple } from 'node/lib/RequestUtils';
import type LegacyContext, { AdvertiserLegacyContextType } from 'node/lib/LegacyContext';
import type { AdvertiserContextType, ClientContext } from 'app/common/lib/context';

export interface UserAgent {
  +browserName: string;
  +browserVersion: string;
  +platform: string;
  +platformVersion: VersionTuple;
  +isTablet: boolean;
  +isMobile: boolean;
  +isRetina: boolean;
}

type ContextProps = {
  context: ClientContext,
  experiments: AExperimentsClient,
  seoExperiments: AExperimentsClient,
  seoUnauthExperiments: AExperimentsClient,
};

export const UserAgentPropTypes = PropTypes.shape({
  browserName: PropTypes.string.isRequired,
  browserVersion: PropTypes.string.isRequired,
  platform: PropTypes.string.isRequired,
  platformVersion: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  isTablet: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isRetina: PropTypes.bool.isRequired,
});

export interface RequestContext {
  +isAmp: boolean;
  +isAuthenticated: boolean;
  +isBot: boolean;
  +language: string;
  +locale: string;
  +unauthId: ?string;
  +origin: string;
  +userAgent: UserAgent;
  +experiments: AExperimentsClient;
  +seoExperiments: AExperimentsClient;
  +seoUnauthExperiments: AExperimentsClient;
  +advertiser: null | AdvertiserContextType;
  +legacyAdvertiser: null | AdvertiserLegacyContextType;
}

export const RequestContextPropType = PropTypes.shape({
  isAmp: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isBot: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  unauthId: PropTypes.string.isRequired,
  origin: PropTypes.string.isRequired,
  userAgent: UserAgentPropTypes.isRequired,
  experiments: PropTypes.object.isRequired,
  seoExperiments: PropTypes.object.isRequired,
  seoUnauthExperiments: PropTypes.object.isRequired,
  // experiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
  // seoExperiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
  // seoUnauthExperiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
});

let _globalRequestContext = null;

function isEmptyObject(obj): boolean {
  return Object.keys(obj).length === 0;
}

function staticRC(): RequestContext {
  return {
    isAmp: false,
    isAuthenticated: false,
    isBot: false,
    language: 'en',
    locale: 'en-US',
    unauthId: 'asdasda',
    origin: 'https://example.com',
    userAgent: {
      browserName: 'Chrome',
      browserVersion: '67.0.3396',
      platform: 'OSX',
      platformVersion: [0, 0, 0],
      isTablet: false,
      isMobile: false,
      isRetina: false,
    },
    experiments: new EmptyExperimentsClient(),
    seoExperiments: new EmptyExperimentsClient(),
    seoUnauthExperiments: new EmptyExperimentsClient(),
    advertiser: null,
    legacyAdvertiser: null,
  };
}

export const RequestContextFactory = {
  fromDesktopContextProps(props: ContextProps): RequestContext {
    const context = props.context;
    return {
      isAmp: false,
      isAuthenticated: context.is_authenticated,
      isBot: context.is_bot === 'true',
      language: context.language,
      locale: context.locale,
      unauthId: context.unauth_id,
      origin: context.origin,
      userAgent: {
        browserName: context.browser_name,
        browserVersion: context.browser_version,
        platform: context.user_agent_platform,
        platformVersion:
          context.user_agent_platform_version && context.user_agent_platform_version.length === 3
            ? context.user_agent_platform_version
            : [0, 0, 0],
        isTablet: context.is_tablet_agent,
        isMobile: context.is_mobile_agent,
        isRetina: context.is_retina,
      },
      experiments: props.experiments,
      seoExperiments: props.seoExperiments,
      seoUnauthExperiments: props.seoUnauthExperiments,
      advertiser: context.advertiser ? context.advertiser : null,
      legacyAdvertiser: null,
    };
  },

  fromLegacyContext(legacyContext: LegacyContext): RequestContext {
    return {
      isAmp: false,
      isAuthenticated: legacyContext.is_authenticated,
      isBot: legacyContext.is_bot === 'true',
      language: legacyContext.language,
      locale: legacyContext.locale,
      unauthId: legacyContext.unauth_id,
      origin: legacyContext.origin,
      userAgent: {
        browserName: legacyContext.browser_name,
        browserVersion: legacyContext.browser_version,
        platform: legacyContext.user_agent_platform,
        platformVersion: legacyContext.user_agent_platform_version || [0, 0, 0],
        isTablet: legacyContext.is_tablet_agent,
        isMobile: legacyContext.is_mobile_agent,
        isRetina: legacyContext.is_retina,
      },
      experiments: legacyContext.experiments,
      // Woah! `seoExperiments` doesn't exist in Node Resources
      seoExperiments: legacyContext.experiments,
      // Woah! `seoUnauthExperiments` doesn't exist in Node Resources
      seoUnauthExperiments: legacyContext.experiments,
      advertiser: null,
      legacyAdvertiser: legacyContext.advertiser ? legacyContext.advertiser : null,
    };
  },

  fromGlobalContext(): RequestContext {
    // early return if we already have a global context set
    if (_globalRequestContext) {
      return _globalRequestContext;
    }
    const requestContext = RequestContextFactory.fromDesktopContextProps({
      context: context.instance,
      ...buildExperimentsClients(context.instance),
    });

    // if the call to fromGlobalContext happens before context.instance is set,
    // make sure we warn (dev only) and don't cache
    if (!isEmptyObject(context.instance)) {
      _globalRequestContext = requestContext;
    } else if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('Using empty context.instance to create RequestContext in fromGlobalContext()');
    }

    return requestContext;
  },

  __private_static_provider_fallback_value(): RequestContext {
    return staticRC();
  },
};

if (process.env.NODE_ENV !== 'production') {
  (RequestContextFactory: any).mock = function(): RequestContext {
    return staticRC();
  };
}
